import { supabase } from '../lib/supabase';

class AudiosChatService {
  // Salvar áudio do usuário no storage e banco
  async salvarAudioUsuario(chatId, mensagemId, alunoId, audioBlob, transcricao, metadata = {}) {
    try {
      console.log('🎤 Salvando áudio do usuário...', { chatId, mensagemId, alunoId });
      
      // 1. Gerar nome único para o arquivo
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `${alunoId}/${chatId}/user_${timestamp}.wav`;
      
      // 2. Upload do arquivo para o storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('chat-audios')
        .upload(fileName, audioBlob, {
          contentType: 'audio/wav',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Erro no upload do áudio:', uploadError);
        throw new Error(`Erro no upload: ${uploadError.message}`);
      }

      console.log('✅ Áudio enviado para storage:', uploadData.path);

      // 3. Obter URL pública (se necessário)
      const { data: urlData } = supabase.storage
        .from('chat-audios')
        .getPublicUrl(fileName);

      // 4. Salvar metadados no banco
      const audioData = {
        mensagem_id: mensagemId,
        chat_id: chatId,
        aluno_id: alunoId,
        tipo_audio: 'user_input',
        arquivo_nome: fileName.split('/').pop(),
        arquivo_path: uploadData.path,
        arquivo_url: urlData.publicUrl,
        arquivo_tamanho: audioBlob.size,
        arquivo_tipo: 'audio/wav',
        duracao_segundos: metadata.duracao || null,
        formato_audio: 'wav',
        sample_rate: metadata.sampleRate || null,
        canais: 1,
        transcricao_texto: transcricao,
        confianca_transcricao: metadata.confidence || null,
        modelo_transcricao: 'whisper-1',
        dispositivo_origem: navigator.userAgent,
        qualidade_audio: 'media',
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          browser: navigator.userAgent.split(' ')[0]
        }
      };

      const { data: dbData, error: dbError } = await supabase
        .from('audios_chat')
        .insert([audioData])
        .select()
        .single();

      if (dbError) {
        console.error('❌ Erro ao salvar no banco:', dbError);
        // Tentar remover arquivo do storage se falhou no banco
        await supabase.storage.from('chat-audios').remove([fileName]);
        throw new Error(`Erro no banco: ${dbError.message}`);
      }

      console.log('✅ Áudio do usuário salvo com sucesso:', dbData.id);
      return {
        success: true,
        audioId: dbData.id,
        fileName: fileName,
        url: urlData.publicUrl
      };

    } catch (error) {
      console.error('❌ Erro ao salvar áudio do usuário:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Salvar áudio da IA (quando disponível)
  async salvarAudioIA(chatId, mensagemId, alunoId, audioBlob, textoResposta, metadata = {}) {
    try {
      console.log('🤖 Salvando áudio da IA...', { chatId, mensagemId, alunoId });
      
      // 1. Gerar nome único para o arquivo
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `${alunoId}/${chatId}/ai_${timestamp}.wav`;
      
      // 2. Upload do arquivo para o storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('chat-audios')
        .upload(fileName, audioBlob, {
          contentType: 'audio/wav',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Erro no upload do áudio da IA:', uploadError);
        throw new Error(`Erro no upload: ${uploadError.message}`);
      }

      console.log('✅ Áudio da IA enviado para storage:', uploadData.path);

      // 3. Obter URL pública
      const { data: urlData } = supabase.storage
        .from('chat-audios')
        .getPublicUrl(fileName);

      // 4. Salvar metadados no banco
      const audioData = {
        mensagem_id: mensagemId,
        chat_id: chatId,
        aluno_id: alunoId,
        tipo_audio: 'ai_response',
        arquivo_nome: fileName.split('/').pop(),
        arquivo_path: uploadData.path,
        arquivo_url: urlData.publicUrl,
        arquivo_tamanho: audioBlob.size,
        arquivo_tipo: 'audio/wav',
        duracao_segundos: metadata.duracao || null,
        formato_audio: 'wav',
        sample_rate: metadata.sampleRate || null,
        canais: 1,
        transcricao_texto: textoResposta,
        modelo_ia: metadata.modelo || 'gpt-4o-realtime-preview-2024-12-17',
        voz_ia: metadata.voz || 'ash',
        parametros_ia: metadata.parametros || {},
        dispositivo_origem: 'OpenAI Realtime API',
        qualidade_audio: 'alta',
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          source: 'openai-realtime'
        }
      };

      const { data: dbData, error: dbError } = await supabase
        .from('audios_chat')
        .insert([audioData])
        .select()
        .single();

      if (dbError) {
        console.error('❌ Erro ao salvar áudio da IA no banco:', dbError);
        // Tentar remover arquivo do storage se falhou no banco
        await supabase.storage.from('chat-audios').remove([fileName]);
        throw new Error(`Erro no banco: ${dbError.message}`);
      }

      console.log('✅ Áudio da IA salvo com sucesso:', dbData.id);
      return {
        success: true,
        audioId: dbData.id,
        fileName: fileName,
        url: urlData.publicUrl
      };

    } catch (error) {
      console.error('❌ Erro ao salvar áudio da IA:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Buscar áudios de um chat
  async buscarAudiosChat(chatId, tipoAudio = null) {
    try {
      console.log('🔍 Buscando áudios do chat:', chatId);
      
      let query = supabase
        .from('audios_chat')
        .select(`
          *,
          mensagens_chat (
            id,
            conteudo,
            data_envio,
            tipo_mensagem
          )
        `)
        .eq('chat_id', chatId)
        .eq('status', 'ativo')
        .order('data_gravacao', { ascending: true });

      if (tipoAudio) {
        query = query.eq('tipo_audio', tipoAudio);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Erro ao buscar áudios:', error);
        throw new Error(error.message);
      }

      console.log(`✅ ${data.length} áudios encontrados`);
      return {
        success: true,
        audios: data
      };

    } catch (error) {
      console.error('❌ Erro ao buscar áudios:', error);
      return {
        success: false,
        error: error.message,
        audios: []
      };
    }
  }

  // Obter estatísticas de áudio por aluno
  async obterEstatisticasAudio(alunoId, dataInicio = null, dataFim = null) {
    try {
      console.log('📊 Obtendo estatísticas de áudio:', alunoId);
      
      const { data, error } = await supabase
        .rpc('estatisticas_audio_aluno', {
          p_aluno_id: alunoId,
          p_data_inicio: dataInicio,
          p_data_fim: dataFim
        });

      if (error) {
        console.error('❌ Erro ao obter estatísticas:', error);
        throw new Error(error.message);
      }

      const stats = data[0] || {
        total_audios: 0,
        total_audios_user: 0,
        total_audios_ia: 0,
        duracao_total_segundos: 0,
        tamanho_total_mb: 0,
        media_duracao_segundos: 0
      };

      console.log('✅ Estatísticas obtidas:', stats);
      return {
        success: true,
        estatisticas: stats
      };

    } catch (error) {
      console.error('❌ Erro ao obter estatísticas:', error);
      return {
        success: false,
        error: error.message,
        estatisticas: null
      };
    }
  }

  // Deletar áudio (marcar como deletado)
  async deletarAudio(audioId) {
    try {
      console.log('🗑️ Deletando áudio:', audioId);
      
      const { data, error } = await supabase
        .from('audios_chat')
        .update({ 
          status: 'deletado',
          updated_at: new Date().toISOString()
        })
        .eq('id', audioId)
        .select()
        .single();

      if (error) {
        console.error('❌ Erro ao deletar áudio:', error);
        throw new Error(error.message);
      }

      console.log('✅ Áudio marcado como deletado');
      return {
        success: true,
        audio: data
      };

    } catch (error) {
      console.error('❌ Erro ao deletar áudio:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Converter AudioBuffer para Blob
  audioBufferToBlob(audioBuffer, mimeType = 'audio/wav') {
    const length = audioBuffer.length;
    const sampleRate = audioBuffer.sampleRate;
    const numberOfChannels = audioBuffer.numberOfChannels;
    
    // Criar buffer WAV
    const buffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(buffer);
    
    // Cabeçalho WAV
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length * 2, true);
    
    // Dados de áudio
    const channelData = audioBuffer.getChannelData(0);
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
    
    return new Blob([buffer], { type: mimeType });
  }

  // Gravar áudio do microfone
  async gravarAudioMicrofone(duracao = 30000) {
    try {
      console.log('🎤 Iniciando gravação do microfone...');
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      const audioChunks = [];
      
      return new Promise((resolve, reject) => {
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          stream.getTracks().forEach(track => track.stop());
          
          console.log('✅ Gravação finalizada:', audioBlob.size, 'bytes');
          resolve({
            success: true,
            audioBlob,
            duracao: duracao / 1000
          });
        };

        mediaRecorder.onerror = (error) => {
          console.error('❌ Erro na gravação:', error);
          stream.getTracks().forEach(track => track.stop());
          reject(error);
        };

        mediaRecorder.start();
        
        // Parar gravação após duração especificada
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        }, duracao);
      });

    } catch (error) {
      console.error('❌ Erro ao gravar áudio:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Exportar instância única
const audiosChatService = new AudiosChatService();
export default audiosChatService;
